import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { callTool } from '../lib/tools'
import useNode from './useNode'
import useOrganizations from './useOrganizations'
import useGraphView from './useGraphView'
import { placeOf, type Located } from '../graph/mapPlaces'
import { newerAnswers, replacePlaces, type MapPoint, type SetPlace } from '../utilities/setPlaces'
import type { Database } from '../types/supabase'

/**
 * The places a person set (nobgp-router#1976): `places`, one for each node whose place was set
 * because its address places it wrong (`node_location_overrides`), and `clouds`, where the map draws
 * each cloud location of the active org (`org_cloud_places`). The graph and the map read the node
 * places over the derived ones (`useMergedData`, `utilities/setPlaces.ts`).
 *
 * The router writes both tables (`node_location_set` and `_clear`: control of the node;
 * `org_cloud_place_set` and `_clear`: an Owner or Admin of the org). The app reads them. After a
 * write the store reads them again, so what is shown is what the router stored (rounded to about
 * 1 km).
 *
 * ⚠ Not kept live by realtime, as `node_metadata` is not: a place changes when a person moves it,
 * in this tab. Another member's move shows at the next load or org switch.
 *
 * ⚠ A read that fails (before router migration 0245 is applied, the tables do not exist) leaves the
 * derived places: the map draws as it did.
 */
export interface SetPlacesState {
  places: SetPlace[]
  clouds: Partial<Record<CloudPlace, MapPoint>>
  get: () => Promise<void>
  /**
   * Read the places of these nodes only: they came into the node store after the last full read
   * (the offline filter, a registration, a realtime event; `useNode.loadNodeDerived`, app#489).
   */
  getForNodes: (nodeIds: string[]) => Promise<void>
  /** Set the same place for each of these nodes. Returns the ids the router refused. */
  setNodes: (
    nodeIds: string[],
    place: { lat: number; lng: number; city?: string; state?: string; country?: string }
  ) => Promise<string[]>
  /** Put these nodes back at the place of their address. Returns the ids the router refused. */
  clearNodes: (nodeIds: string[]) => Promise<string[]>
  placeCloud: (cloud: CloudPlace, point: MapPoint) => Promise<void>
  clearCloud: (cloud: CloudPlace) => Promise<void>
  reset: () => void
}

const state: Pick<SetPlacesState, 'places' | 'clouds'> = { places: [], clouds: {} }

/** Each call, one node at a time, and the ids whose call failed. */
async function eachNode(nodeIds: string[], call: (nodeId: string) => Promise<unknown>): Promise<string[]> {
  const results = await Promise.allSettled(nodeIds.map(call))
  return nodeIds.filter((_, i) => results[i].status === 'rejected')
}

/**
 * After a node's place changes, the viewer's place can change too: the router reports a place set
 * for a node behind the viewer's own address as the viewer's (`whoami`, "it follows").
 *
 * ⚠ The viewer's place stays as it is until the answer comes. `loadViewer` holds `null` during its
 * call, and with `null` the map removes the home icon and every node with no place, and fits the
 * frame again, two times for each save.
 */
async function askViewerAgain() {
  try {
    const who = await callTool<{ location?: Located }>('whoami')
    useGraphView.setState({ viewer: placeOf(who?.location) })
  } catch (error) {
    console.warn('set places: the viewer location could not be read again', error)
  }
}

// ⚠ Each read takes a number, and only the LATEST one may write the store: the read after a save
// and the read of an org switch can overlap, and an older answer holds the other org's clouds.
let latestRead = 0

const readNodes = (nodeIds: string[]) =>
  supabase.from('node_location_overrides').select('node_id, latitude, longitude, city, state, country').in('node_id', nodeIds)

type NodePlaceRow = Pick<
  Database['public']['Tables']['node_location_overrides']['Row'],
  'node_id' | 'latitude' | 'longitude' | 'city' | 'state' | 'country'
>

// ⚠ Each read of the node places takes a number, and the store keeps, for each node, the number of
// the read whose answer it holds (`newerAnswers`, app#490). Two reads can ask for one node, and an
// older answer that lands last must not change it.
let nodeReads = 0
const answeredBy = new Map<string, number>()

const placesOf = (rows: NodePlaceRow[] | null): SetPlace[] =>
  (rows ?? []).map(row => ({
    nodeId: row.node_id,
    latitude: Number(row.latitude),
    longitude: Number(row.longitude),
    city: row.city,
    state: row.state,
    country: row.country,
  }))

const heldNodes = () => new Set(useNode.getState().nodes.map(node => node.id))

const useSetPlaces = create<SetPlacesState>()((set, get) => {
  const read = async () => {
    const seq = ++latestRead
    const nodeRead = ++nodeReads
    const nodeIds = useNode.getState().nodes.map(node => node.id)
    const orgId = useOrganizations.getState().activeOrgId
    const [nodes, clouds] = await Promise.all([
      nodeIds.length ? readNodes(nodeIds) : Promise.resolve({ data: [], error: null }),
      orgId
        ? supabase.from('org_cloud_places').select('cloud, latitude, longitude').eq('org_id', orgId)
        : Promise.resolve({ data: [], error: null }),
    ])
    if (seq !== latestRead) return
    if (nodes.error) console.warn('set places: the node places could not be read', nodes.error.message)
    if (clouds.error) console.warn('set places: the cloud places could not be read', clouds.error.message)
    set({
      // ⚠ Only the places of the nodes this read asked for: a node that came in during the read
      // has its own read (`getForNodes`), and this answer must not remove what that read brought.
      places: nodes.error
        ? get().places
        : replacePlaces(get().places, placesOf(nodes.data), newerAnswers(nodeIds, nodeRead, answeredBy), heldNodes()),
      clouds: clouds.error
        ? get().clouds
        : Object.fromEntries(
            (clouds.data ?? [])
              .filter(row => row.cloud === 'grok' || row.cloud === 'nobgp')
              .map(row => [row.cloud, { lat: Number(row.latitude), lng: Number(row.longitude) }])
          ),
    })
  }

  return {
    ...state,
    // ⚠ Never rejects: it runs in the controller's batch and in the org switch, and one rejection
    // would reject the whole batch (`controller.ts`).
    get: async () => {
      try {
        await read()
      } catch (error) {
        console.warn('set places: the places could not be read', error)
      }
    },
    // ⚠ Never rejects, as `get`. Not `latestRead`: a full read after it must not drop its answer
    // whole. The node read number (`newerAnswers`) decides for each node.
    getForNodes: async nodeIds => {
      if (!nodeIds.length) return
      const nodeRead = ++nodeReads
      try {
        const { data, error } = await readNodes(nodeIds)
        if (error) return console.warn('set places: the node places could not be read', error.message)
        set({ places: replacePlaces(get().places, placesOf(data), newerAnswers(nodeIds, nodeRead, answeredBy), heldNodes()) })
      } catch (error) {
        console.warn('set places: the node places could not be read', error)
      }
    },
    setNodes: async (nodeIds, place) => {
      const refused = await eachNode(nodeIds, nodeId =>
        callTool('node_location_set', {
          node_id: nodeId,
          latitude: place.lat,
          longitude: place.lng,
          ...(place.city ? { city: place.city } : {}),
          ...(place.state ? { state: place.state } : {}),
          ...(place.country ? { country: place.country } : {}),
        })
      )
      await get().get()
      void askViewerAgain()
      return refused
    },
    clearNodes: async nodeIds => {
      const refused = await eachNode(nodeIds, nodeId => callTool('node_location_clear', { node_id: nodeId }))
      await get().get()
      void askViewerAgain()
      return refused
    },
    placeCloud: async (cloud, point) => {
      const orgId = useOrganizations.getState().activeOrgId
      if (!orgId) return
      await callTool('org_cloud_place_set', { org_id: orgId, cloud, latitude: point.lat, longitude: point.lng })
      await get().get()
    },
    clearCloud: async cloud => {
      const orgId = useOrganizations.getState().activeOrgId
      if (!orgId) return
      await callTool('org_cloud_place_clear', { org_id: orgId, cloud })
      await get().get()
    },
    reset: () => set({ ...state }),
  }
})

export default useSetPlaces
